import React from 'react'
import {useInView} from 'react-intersection-observer'
import styled from 'styled-components'

import Image from '@/components/image'
import {ImagesMarquee} from '@/components/images-marquee'
import PrismicLink from '@/components/prismic-link'
import {Text, TextLink} from '@/components/text'
import useMediaQuery from '@/hooks/use-media-query'
import {Section, BackgroundWrapper, Container} from '@/styles/grid'
import {isValidBackgroundColor} from '@/utils/is-valid-background-color'
import mq from '@/utils/media-query'

import type {Props as PrismicLinkProps} from '@/components/prismic-link'
import type {ImageSource} from '@/types/image'

const config = {
    logoWidth: 100,
    spacing: 40,
}

interface StatItem {
    highlight: string
    body: string
}

export interface Props {
    logos: ImageSource[]
    title?: string
    ctaLink: PrismicLinkProps
    ctaText: string
    backgroundColor?: string
    isMarquee?: boolean
    stats?: StatItem[]
}

export const CustomerLogosWithCta = React.forwardRef<HTMLDivElement, Props>(
    ({logos, title, ctaLink, ctaText, stats, backgroundColor = '', isMarquee = false}, ref) => {
        const bgColor = isValidBackgroundColor(backgroundColor) ? backgroundColor : 'muted'
        const isSmallScreen = useMediaQuery.max('sm')
        const [inViewRef, isInView] = useInView({triggerOnce: true, skip: Boolean(!stats)})

        return (
            <BackgroundWrapper ref={ref} $color={bgColor}>
                <StyledSection edgeToEdge={isMarquee} hideOverflow>
                    {title && (
                        <StyledContainer>
                            <Text $center $variant="title-base">
                                {title}
                            </Text>
                        </StyledContainer>
                    )}
                    <CtaLogosContainer>
                        {isMarquee || isSmallScreen ? (
                            <ImagesMarquee
                                images={logos}
                                backgroundColor={bgColor}
                                itemWidth={100}
                            />
                        ) : (
                            <CtaLogosGrid>
                                {logos.map((logo, i) => (
                                    <Logo key={`${logo.url}-${i}`} image={logo} height={60} />
                                ))}

                                <StyledTextLink
                                    $variant="paragraph-large"
                                    as={PrismicLink}
                                    {...ctaLink}
                                >
                                    {ctaText}
                                </StyledTextLink>
                            </CtaLogosGrid>
                        )}
                    </CtaLogosContainer>

                    {stats && (
                        <StatsWrapper ref={inViewRef}>
                            {stats.map((stat, i) => (
                                <Stat
                                    key={`${stat.highlight}-${i}`}
                                    highlight={stat.highlight}
                                    body={stat.body}
                                    idx={i}
                                    inView={isInView}
                                />
                            ))}
                        </StatsWrapper>
                    )}
                </StyledSection>
            </BackgroundWrapper>
        )
    },
)

interface StatProps {
    highlight: string
    body: string
    idx: number
    inView: boolean
}

const Stat = ({highlight, body, inView, idx}: StatProps) => (
    <StatGroup
        style={
            {
                '--translateY': inView ? '0px' : '40px',
                '--opacity': inView ? 1 : 0,
                '--idx': idx,
            } as React.CSSProperties
        }
    >
        <Text $variant="hero" translate="no">
            {highlight}
        </Text>
        <Text $color="muted">{body}</Text>
    </StatGroup>
)

const StyledSection = styled(Section)`
    ${mq.max('sm')} {
        max-width: none;
        padding-right: 0;
        padding-left: 0;
    }
`

const StyledContainer = styled(Container)`
    margin-bottom: ${({theme}) => theme.spacings.s32};
`

const Logo = styled(Image)`
    width: ${config.logoWidth}px;
    min-width: ${config.logoWidth}px;
    height: 60px;
    object-fit: contain;

    ${mq.max('sm')} {
        margin: 0 ${config.spacing}px;
    }
`

const CtaLogosContainer = styled(Container)`
    display: grid;
    align-items: center;
    padding-right: 0;
    padding-left: 0;
`

const CtaLogosGrid = styled.div`
    display: grid;
    grid-auto-flow: column;
    justify-content: space-between;
    align-items: center;
`

const StyledTextLink = styled(TextLink)`
    ${mq.max('sm')} {
        display: none;
    }
`

const StatsWrapper = styled(Container)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: ${({theme}) => theme.spacings.s20};
    margin-top: ${({theme}) => theme.spacings.s40};

    ${mq.min('sm')} {
        margin-top: 80px;
        flex-direction: row;
        gap: ${({theme}) => theme.spacings.s40};
    }
`

const StatGroup = styled.div`
    flex: 1;
    display: grid;
    align-items: center;
    text-align: center;
    background-color: ${({theme}) => theme.colors.background.muted};
    border-radius: ${({theme}) => theme.radii.arc20};
    padding: ${({theme}) => theme.spacings.s20};
    text-wrap: pretty;

    ${mq.min('sm')} {
        padding: ${({theme}) => `${theme.spacings.s40} ${theme.spacings.s56}`};
        transform: translateY(var(--translateY));
        opacity: var(--opacity);
        transition: ${({theme}) =>
            `opacity ${theme.transitions.smoothIn}, transform ${theme.transitions.smoothIn}`};
        transition-delay: calc(200ms + calc(var(--idx) * 100ms));
    }
`
